/**
 * Visões de gestão — as perguntas que o acompanhamento diário não responde.
 *
 * As quatro telas originais respondem "o que aconteceu com este arquivo".
 * Estas respondem outra coisa: **quanto tempo a operadora leva para pagar**,
 * **quanto do que entrou o sistema conseguiu atribuir a um protocolo**, e
 * **como cada convênio se comporta** — perguntas de decisão, não de conferência.
 */
import { useEffect, useState } from 'react';
import { mensagemErro } from '../api/axios';
import {
  evolucaoMensal,
  indicadoresGestao,
  visaoAtendimentoXCompetencia,
  visaoOperadoras,
} from '../api/fluxfin';
import { Ajuda, Alerta, Badge, Indicador, LoadingSpinner, Painel, Tabela, Th, Vazio } from './common';
import { brl, numero } from '../utils/formato';
import { useOrdenacao } from '../hooks/useOrdenacao';

/* ── Atendimento × competência ──────────────────────────────────────────── */
function MatrizDefasagem({ dados }) {
  if (!dados?.celulas?.length) {
    return (
      <Painel titulo="Atendimento × competência do pagamento">
        <Vazio
          icone="bi-calendar3"
          titulo="Ainda sem dados"
          descricao="A matriz aparece quando houver arquivos confirmados com data de atendimento."
        />
      </Painel>
    );
  }

  const valorDe = (atend, comp) =>
    dados.celulas.find((c) => c.atendimento === atend && c.competencia === comp);

  // A intensidade dá a leitura em diagonal: onde o dinheiro se concentra.
  const maior = Math.max(...dados.celulas.map((c) => Number(c.valor)));

  return (
    <Painel
      titulo="Atendimento × competência do pagamento"
      subtitulo="Quanto de cada mês de atendimento foi pago em cada competência. A distância entre os dois é o prazo real de recebimento."
      acoes={
        dados.defasagem_media_meses != null && (
          <Badge tom={dados.defasagem_media_meses > 4 ? 'pendente' : 'confirmado'}>
            {dados.defasagem_media_meses} meses em média
          </Badge>
        )
      }
    >
      <div style={{ overflowX: 'auto' }}>
        <Tabela estreita>
          <thead>
            <tr>
              <Th>
                Atendimento
                <Ajuda titulo="Mês do atendimento" origem="Arquivo da operadora · Data">
                  Quando o exame foi feito. Não confundir com a competência, que é quando
                  a operadora pagou — as duas quase nunca coincidem.
                </Ajuda>
              </Th>
              {dados.competencias.map((c) => (
                <Th key={c} alinhar="right">
                  {c}
                </Th>
              ))}
              <Th alinhar="right">Total</Th>
            </tr>
          </thead>
          <tbody>
            {dados.meses_atendimento.map((atend) => {
              const linha = dados.competencias.map((c) => valorDe(atend, c));
              const total = linha.reduce((s, c) => s + Number(c?.valor || 0), 0);
              return (
                <tr key={atend}>
                  <td className="col-principal">{atend}</td>
                  {linha.map((celula, i) => (
                    <td
                      key={dados.competencias[i]}
                      className="col-valor"
                      style={
                        celula
                          ? {
                              background: `rgba(47,163,108,${
                                0.06 + (Number(celula.valor) / maior) * 0.22
                              })`,
                            }
                          : undefined
                      }
                      title={
                        celula
                          ? `${celula.qtd_guias} guias · ${celula.defasagem_meses} meses até o pagamento`
                          : undefined
                      }
                    >
                      {celula ? brl(celula.valor) : '—'}
                      {celula?.defasagem_meses != null && (
                        <div style={{ fontSize: 11, color: 'var(--ff-muted)' }}>
                          {celula.defasagem_meses} {celula.defasagem_meses === 1 ? 'mês' : 'meses'}
                        </div>
                      )}
                    </td>
                  ))}
                  <td className="col-valor">
                    <strong>{brl(total)}</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Tabela>
      </div>
      <p className="p-3 m-0" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
        Uma célula longe da diagonal é recebimento que demorou. A média é ponderada pelo
        valor — um atraso grande em pouco dinheiro não distorce o número.
      </p>
    </Painel>
  );
}

/* ── Por convênio, com os protocolos dentro ─────────────────────────────── */
function PorConvenio({ operadoras }) {
  const ord = useOrdenacao(operadoras);
  const [abertas, setAbertas] = useState(() => new Set());

  function alternar(id) {
    setAbertas((atual) => {
      const nova = new Set(atual);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });
  }

  if (!operadoras?.length) {
    return (
      <Painel titulo="Por convênio">
        <Vazio
          icone="bi-building"
          titulo="Ainda sem conciliação"
          descricao="Os convênios aparecem aqui quando as guias resolverem para protocolos."
        />
      </Painel>
    );
  }

  return (
    <Painel
      titulo="Por convênio"
      subtitulo="Clique para abrir os protocolos de cada operadora."
    >
      <Tabela>
        <thead>
          <tr>
            <Th campo="nome" estado={ord}>Convênio</Th>
            <Th campo="qtd_protocolos" estado={ord} alinhar="right">Protocolos</Th>
            <Th campo="qtd_guias" estado={ord} alinhar="right">Guias</Th>
            <Th campo="total_pago" estado={ord} alinhar="right">A baixar</Th>
            <Th campo="total_cobrado" estado={ord} alinhar="right">Cobrado</Th>
            <Th campo="diferenca" estado={ord} alinhar="right">Diferença</Th>
          </tr>
        </thead>
        <tbody>
          {ord.ordenados.map((o) => {
            const aberta = abertas.has(o.id_operadora);
            return [
              <tr
                key={o.id_operadora}
                onClick={() => alternar(o.id_operadora)}
                style={{ cursor: 'pointer' }}
              >
                <td className="col-principal">
                  <i
                    className={`bi ${aberta ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}
                    aria-hidden="true"
                  />
                  {o.operadora}
                  {o.qtd_manuais > 0 && (
                    <span
                      className="ms-2"
                      style={{ fontSize: 12, color: '#9a5b06' }}
                      title="Inclui guias vinculadas ao protocolo manualmente."
                    >
                      <i className="bi bi-pencil-square me-1" aria-hidden="true" />
                      {o.qtd_manuais}
                    </span>
                  )}
                </td>
                <td className="col-qtd">{numero(o.qtd_protocolos)}</td>
                <td className="col-qtd">{numero(o.qtd_guias)}</td>
                <td className="col-valor">
                  <strong>{brl(o.valor_baixa)}</strong>
                </td>
                <td className="col-valor">{brl(o.valor_cobrado)}</td>
                <td className="col-valor">{o.diferenca != null ? brl(o.diferenca) : '—'}</td>
              </tr>,
              ...(aberta
                ? o.protocolos.map((p) => (
                    <tr key={`${o.id_operadora}-${p.protocolo}`} style={{ background: 'var(--ff-surface-2, #f8fafc)' }}>
                      <td className="col-codigo" style={{ paddingLeft: 40 }}>
                        {p.protocolo}
                        {p.qtd_manuais > 0 && (
                          <span
                            className="ms-2"
                            style={{ fontSize: 12, color: '#9a5b06' }}
                            title="Guias vinculadas manualmente."
                          >
                            <i className="bi bi-pencil-square" aria-hidden="true" />
                          </span>
                        )}
                      </td>
                      <td />
                      <td className="col-qtd">{numero(p.qtd_guias)}</td>
                      <td className="col-valor">{brl(p.valor_baixa)}</td>
                      <td className="col-valor">{brl(p.valor_cobrado)}</td>
                      <td className="col-valor">
                        {p.diferenca != null ? brl(p.diferenca) : '—'}
                      </td>
                    </tr>
                  ))
                : []),
            ];
          })}
        </tbody>
      </Tabela>
    </Painel>
  );
}

/* ── Evolução mensal ────────────────────────────────────────────────────── */
function Evolucao({ linhas }) {
  if (!linhas?.length) return null;
  const maior = Math.max(...linhas.map((l) => Number(l.valor)));

  return (
    <Painel
      titulo="Evolução do recebimento"
      subtitulo="Quanto entrou em cada competência."
    >
      <div className="p-4 d-flex flex-column gap-2">
        {linhas.map((l) => (
          <div key={l.competencia} className="d-flex align-items-center gap-3">
            <span className="ff-mono" style={{ minWidth: 64, fontSize: 13 }}>
              {l.competencia}
            </span>
            <div
              style={{
                flex: 1,
                height: 22,
                background: 'var(--ff-surface-2, #f1f5f9)',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(Number(l.valor) / maior) * 100}%`,
                  height: '100%',
                  background: 'var(--ff-blue)',
                  borderRadius: 4,
                }}
                title={`${l.qtd_guias} guias em ${l.qtd_arquivos} arquivo(s)`}
              />
            </div>
            <span style={{ minWidth: 120, textAlign: 'right', fontSize: 13 }}>
              {brl(l.valor)}
            </span>
          </div>
        ))}
      </div>
    </Painel>
  );
}

export default function VisoesEstrategicas() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    Promise.all([
      indicadoresGestao(),
      visaoAtendimentoXCompetencia(),
      visaoOperadoras(),
      evolucaoMensal(),
    ])
      .then(([i, m, o, e]) =>
        setDados({
          indicadores: i.data,
          matriz: m.data,
          operadoras: o.data,
          evolucao: e.data,
        })
      )
      .catch((err) => setErro(mensagemErro(err, 'Não foi possível carregar as visões.')));
  }, []);

  if (erro) return <Alerta tipo="erro">{erro}</Alerta>;
  if (!dados) return <LoadingSpinner />;

  const i = dados.indicadores;

  return (
    <>
      <div className="ff-grid-tiles mb-4">
        <Indicador
          natureza="financeiro"
          rotulo="Recebido no período"
          valor={brl(i.total_recebido)}
          nota="soma dos arquivos confirmados"
        />
        <Indicador
          natureza={i.taxa_conciliacao >= 99 ? 'positivo' : 'atencao'}
          rotulo="Taxa de conciliação"
          valor={i.taxa_conciliacao != null ? `${i.taxa_conciliacao}%` : '—'}
          nota={`${brl(i.nao_conciliado)} sem protocolo`}
        />
        <Indicador
          natureza={i.defasagem_media_meses > 4 ? 'atencao' : 'neutro'}
          rotulo="Prazo médio de recebimento"
          valor={
            i.defasagem_media_meses != null ? `${i.defasagem_media_meses} meses` : '—'
          }
          nota="do atendimento ao pagamento"
        />
        <Indicador
          natureza="neutro"
          rotulo="Convênios"
          valor={numero(i.qtd_operadoras)}
          nota="com recebimento conciliado"
        />
      </div>

      <MatrizDefasagem dados={dados.matriz} />
      <div className="mt-3">
        <PorConvenio operadoras={dados.operadoras} />
      </div>
      <div className="mt-3">
        <Evolucao linhas={dados.evolucao} />
      </div>
    </>
  );
}
